import { urlToFile } from 'common/Utils/fileUtils'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { analyzeStepOpenCascade, createPartStepAnalysis } from 'store/actions'
import { extractCadErrorName } from './extractCadErrorName'

/**
@param {{
	partId: number
	stepModelSrc: string
	initialStepAnalysis: StepAnalysis | 'fetching...'
	showError: (errorMessageTranslationKey: StlViewerError) => void
}} args
 */
export const useAnalyzeStep = ({ partId, stepModelSrc, initialStepAnalysis, showError }) => {

	const [updatedStepAnalysis, setUpdatedStepAnalysis] = useState(initialStepAnalysis === 'fetching...' ? null : initialStepAnalysis)

	const { createAnalysisStatus, analyzeStepOpenCascadeState } = useSelector(state => (/** @type {StoreState} */(state)).Parts)

	const dispatch = useDispatch()

	// Ref to prevent concurrent analysis calls
	const isAnalyzingRef = useRef(false)

	useEffect(() => {
		if (initialStepAnalysis === 'fetching...') {
			// Still checking if stepAnalysis already exists in database:
			setUpdatedStepAnalysis(null)
			return
		}
		if (stepModelSrc && !updatedStepAnalysis && !isAnalyzingRef.current) {
			// stepAnalysis does not exist in database. Fetch the file and analyze:
			isAnalyzingRef.current = true
			const urlWithoutQuery = stepModelSrc.split('?')[0]
			let filename = urlWithoutQuery.split('/').pop() || 'model.step'
			filename = decodeURIComponent(filename)
			urlToFile(stepModelSrc, filename).then(stepFile => {
				dispatch(analyzeStepOpenCascade({ partId, stepFile }, { id: partId }))
			}).catch(() => {
				// urlToFile failed (network/fetch issue unrelated to the model itself): Reset so
				// the analysis is not left in a permanently-stuck state, but don't surface a
				// misleading "could not analyze STEP" error to the user.
				isAnalyzingRef.current = false
			})
		}
	}, [initialStepAnalysis, stepModelSrc])

	useEffect(() => {
		if (!isAnalyzingRef.current) return
		const sliceEntry = analyzeStepOpenCascadeState[partId]
		if (!sliceEntry || sliceEntry.status !== 'finished') return

		isAnalyzingRef.current = false

		if (sliceEntry.error) {
			const key = /** @type {StlViewerError} */ (extractCadErrorName(sliceEntry.error) ?? 'could_not_analyze_step')
			showError(key)
			return
		}

		const { stepAnalysis, stepAnalysisBase64 } = sliceEntry.response
		if (stepAnalysis) {
			setUpdatedStepAnalysis(stepAnalysis)
			if (createAnalysisStatus?.[partId] == null) {
				dispatch(createPartStepAnalysis(partId, stepAnalysisBase64))
			}
		}
	}, [analyzeStepOpenCascadeState, partId])

	return {
		updatedStepAnalysis,
	}
}
